import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isAuthenticated, getUserId as authGetUserId, login as authLogin, logout as authLogout } from '../../services/auth';
import { createThread } from '../../services/chatApi';
import {
  chat, getUserChatSessions, createUserChatSession, getSessionChatHistory,
  updateChatSessionName, deleteChatSession, submitChatFeedback,
  type ChatResponse, type ChatSessionSummary, type ChatHistoryItem, type ChatFeedbackRequest, type ContextItem, type RunProgressPayload
} from '../../services/chatApi';
import { TopBar } from './components/TopBar';
import { Sidebar } from './components/Sidebar';
import { MessageList } from './components/MessageList';
import { TitleModal, FeedbackModal, SourcesModal } from './components/Modals';
import { formatDate } from './utils';
import type { UiMessage, FeedbackType } from './types';
import { notifyError, notifyInfo } from '../../shared/notify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';

export default function ChatApp() {
  const { t } = useTranslation();
  
  const [authed, setAuthed] = useState(isAuthenticated());
  const [userId, setUserId] = useState<string | undefined>(authGetUserId());

  // Refresh userId when auth state changes
  useEffect(() => { setUserId(authGetUserId()); }, [authed]);

  // Sessions
  const [sessions, setSessions] = useState<ChatSessionSummary[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | undefined>();

  // Messages
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [input, setInput] = useState('');
  const [waiting, setWaiting] = useState(false);
  const [includeMail, setIncludeMail] = useState(true);

  // UI
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [titleModal, setTitleModal] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const titleRef = useRef<HTMLInputElement>(null);

  // Feedback
  const [feedbackModal, setFeedbackModal] = useState<{ messageId: string; type: FeedbackType } | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackGiven, setFeedbackGiven] = useState<Record<string, FeedbackType | undefined>>({});

  // Sources
  const [sourcesModal, setSourcesModal] = useState<ContextItem[] | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const streamingAssistantIdRef = useRef<string | null>(null);
  const streamingPendingUpdateRef = useRef<Partial<UiMessage> | null>(null);
  const streamingFrameRef = useRef<number | null>(null);
  const streamingTextRef = useRef<string>('');

  // Load sessions
  useEffect(() => {
    const onErr = (ev: ErrorEvent) => notifyError(t('chat.errors.unexpected'), ev.error?.message || ev.message);
    const onRej = (ev: PromiseRejectionEvent) => notifyError(t('chat.errors.unexpected'), (ev.reason && (ev.reason.message || String(ev.reason))) || '');
    window.addEventListener('error', onErr);
    window.addEventListener('unhandledrejection', onRej);

    let cancelled = false;

    (async () => {
      try {
        if (!authed || !userId) {
          setSessions([]); setActiveSessionId(undefined); setMessages([]);
          return;
        }

        const existing = await getUserChatSessions(userId);
        if (cancelled) return;

        const created = await createUserChatSession(userId);
        if (cancelled) return;

        const combined = [created, ...existing];
        setSessions(combined);
        setActiveSessionId(created.id);
        setMessages([]);
      } catch (err) {
        console.warn(t('chat.errors.loadSessionsFailed'), err);
      }
    })();

    return () => {
      cancelled = true;
      window.removeEventListener('error', onErr);
      window.removeEventListener('unhandledrejection', onRej);
    };
  }, [authed, userId, t]);

  // Load history
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!activeSessionId) { setMessages([]); return; }
      try {
        const history = await getSessionChatHistory(activeSessionId);
        if (cancelled) return;
        const mapped: UiMessage[] = history
          .map((h: ChatHistoryItem): UiMessage => ({
            id: h.id, role: (h.role === 'user' ? 'user' : 'assistant'),
            text: h.message, createdAt: h.timestamp ? new Date(h.timestamp) : new Date(), sources: h.contexts
          }))
          .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
        setMessages(mapped);
      } catch { setMessages([]); }
    })();
    return () => { cancelled = true; };
  }, [activeSessionId]);

  const activeSession = sessions.find((s) => s.id === activeSessionId);
  const displayedTitle = activeSession?.name || (activeSession?.creationTime ? formatDate(activeSession.creationTime) : t('chat.session.defaultTitle'));

  // Title
  const openTitle = () => { setTitleDraft(displayedTitle); setTitleModal(true); setTimeout(() => titleRef.current?.focus(), 40); };
  const saveTitle = async () => {
    if (!activeSessionId) return setTitleModal(false);
    const name = titleDraft.trim(); if (!name) return setTitleModal(false);
    try {
      await updateChatSessionName(activeSessionId, name);
      setSessions((prev) => prev.map((s) => (s.id === activeSessionId ? { ...s, name } : s)));
    } catch {}
    setTitleModal(false);
  };

  // New/delete session
  const startNewChat = async () => {
    if (!authed || !userId) { await authLogin().catch(() => {}); setAuthed(isAuthenticated()); setUserId(authGetUserId()); return; }
    const created = await createUserChatSession(userId);
    setSessions((prev) => [created, ...prev]); setActiveSessionId(created.id); setMessages([]);
  };
  const removeSession = async (id: string) => {
    try { await deleteChatSession(id); } catch {}
    const rest = sessions.filter((s) => s.id !== id);
    setSessions(rest);
    if (activeSessionId === id) { setActiveSessionId(rest[0]?.id); setMessages([]); }
  };

  // Vind laatste server-id (geen lokale 'u-'/'a-'/'err-' ids)
  const lastPersistedId = (arr: UiMessage[]): string | null => {
    for (let i = arr.length - 1; i >= 0; i--) {
      const id = arr[i].id;
      if (id && !/^u-|^a-|^err-/.test(id)) return id;
    }
    return null;
  };

  // Send
  async function fetchMailContext(): Promise<string | undefined> {
    const OfficeAny: any = (window as any).Office;
    if (!OfficeAny || !OfficeAny.context || !OfficeAny.context.mailbox || !OfficeAny.context.mailbox.item) {
      notifyInfo(t('chat.messages.noEmailContext'));
      return undefined;
    }
    const item: any = OfficeAny.context.mailbox.item;

    const getSubject = () => new Promise<string | undefined>((resolve) => {
      try {
        if (typeof item.subject === 'string') return resolve(item.subject as string);
        if (item.subject?.getAsync) {
          item.subject.getAsync((r: any) => resolve(r.status === OfficeAny.AsyncResultStatus.Succeeded ? r.value : undefined));
          return;
        }
      } catch {}
      resolve(undefined);
    });

    const getBodyCompose = () => new Promise<string | undefined>((resolve) => {
      try {
        if (item.body?.getAsync) {
          item.body.getAsync('text', (r: any) => resolve(r.status === OfficeAny.AsyncResultStatus.Succeeded ? String(r.value || '') : undefined));
          return;
        }
      } catch {}
      resolve(undefined);
    });

    const getSelectedRead = () => new Promise<string | undefined>((resolve) => {
      try {
        if (item.getSelectedDataAsync) {
          item.getSelectedDataAsync('text', (r: any) => resolve(r.status === OfficeAny.AsyncResultStatus.Succeeded ? String(r.value?.data || '') : undefined));
          return;
        }
      } catch {}
      resolve(undefined);
    });

    const emailsFrom = () => {
      try {
        const from = item.from?.emailAddress || item.from?.displayName;
        return from ? String(from) : undefined;
      } catch { return undefined; }
    };
    const emailsList = (arr: any): string | undefined => {
      try {
        if (!arr) return undefined;
        const list = (Array.isArray(arr) ? arr : []).map((x) => x?.emailAddress || x?.displayName).filter(Boolean);
        return list.length ? list.join(', ') : undefined;
      } catch { return undefined; }
    };

    const [subject, bodyCompose, selRead] = await Promise.all([getSubject(), getBodyCompose(), getSelectedRead()]);
    const from = emailsFrom();
    const to = emailsList(item.to);
    const cc = emailsList(item.cc);

    const body = bodyCompose || selRead;
    const bodyTrim = body ? (body.length > 4000 ? body.slice(0, 4000) + '\n...[afgekapt]' : body) : undefined;

    const parts: string[] = [];
    if (subject) parts.push(`Onderwerp: ${subject}`);
    if (from) parts.push(`Van: ${from}`);
    if (to) parts.push(`Aan: ${to}`);
    if (cc) parts.push(`CC: ${cc}`);
    if (bodyTrim) parts.push(`Inhoud:\n${bodyTrim}`);

    const ctx = parts.join('\n');
    return ctx || undefined;
  }

  const handleSend = async () => {
    const text = input.trim(); if (!text) return;
    if (!authed || !userId) { await authLogin().catch(() => {}); setAuthed(isAuthenticated()); setUserId(authGetUserId()); return; }

    const parentId = lastPersistedId(messages);
    const tempId = 'u-' + Date.now();
    const assistantTempId = 'a-' + Date.now();
    streamingAssistantIdRef.current = assistantTempId;
    streamingPendingUpdateRef.current = null;
    if (streamingFrameRef.current !== null) {
      cancelAnimationFrame(streamingFrameRef.current);
      streamingFrameRef.current = null;
    }
    streamingTextRef.current = '';

    let sendText = text;
    if (includeMail) {
      try {
        const ctx = await fetchMailContext();
        if (ctx) {
          sendText = `[[EMAIL_CONTEXT_START]]\n${ctx}\n[[EMAIL_CONTEXT_END]]\n\n${text}`;
        } else {
          notifyInfo(t('chat.messages.noEmailContext'));
        }
      } catch (e) {
        notifyError(t('chat.messages.emailReadFailed'), (e as any)?.message || String(e));
      }
    }

    const userMsg: UiMessage = { id: tempId, role: 'user', text: sendText, createdAt: new Date() };
    const assistantMsg: UiMessage = { id: assistantTempId, role: 'assistant', text: '', createdAt: new Date(), sources: [] };
    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setInput('');
    setWaiting(true);

    // Als er geen actieve sessie is, maak eerst een nieuwe thread aan
    let sessionId = activeSession?.id || null;
    if (!sessionId && userId) {
      try {
        console.log('[ChatApp] ' + t('chat.errors.threadCreationFailed') + '...');
        const newThread = await createThread(userId, t('chat.session.newConversation'));
        sessionId = newThread.threadId;
        
        const newSession: ChatSessionSummary = {
          id: newThread.threadId,
          name: newThread.title,
          creationTime: newThread.createdAt
        };
        setSessions((prev) => [newSession, ...prev]);
        setActiveSessionId(sessionId);
      } catch (err) {
        console.error('[ChatApp] ' + t('chat.errors.threadCreationFailed') + ':', err);
        notifyError(t('chat.errors.threadCreationFailed'), (err as any)?.message || String(err));
        setWaiting(false);
        return;
      }
    }

    const payload: any = { userId, sessionId, message: { parentId, message: sendText, role: 'user' } };
    const controller = new AbortController();
    abortRef.current = controller;

    const flushAssistantUpdate = () => {
      const pending = streamingPendingUpdateRef.current;
      if (!pending) return;
      const targetId = streamingAssistantIdRef.current ?? assistantTempId;
      setMessages((prev) => {
        let updated = false;
        const next = prev.map((m) => {
          if (m.id === targetId) {
            const merged: UiMessage = { ...m, ...pending };
            if (pending.id && pending.id !== m.id) {
              merged.id = pending.id;
              streamingAssistantIdRef.current = pending.id;
            }
            updated = true;
            return merged;
          }
          if (pending.id && m.id === pending.id) {
            const merged: UiMessage = { ...m, ...pending };
            streamingAssistantIdRef.current = pending.id;
            updated = true;
            return merged;
          }
          return m;
        });

        if (!updated && pending.id) {
          // Als het bericht inmiddels een ander id heeft, probeer die alsnog bij te werken
          return next.map((m) => {
            if (m.id === pending.id) {
              const merged: UiMessage = { ...m, ...pending };
              streamingAssistantIdRef.current = pending.id as string;
              return merged;
            }
            return m;
          });
        }

        return next;
      });
      streamingPendingUpdateRef.current = null;
    };

    const scheduleAssistantUpdate = (update: Partial<UiMessage>, immediate = false) => {
      streamingPendingUpdateRef.current = { ...(streamingPendingUpdateRef.current || {}), ...update };

      if (immediate) {
        if (streamingFrameRef.current !== null) {
          cancelAnimationFrame(streamingFrameRef.current);
          streamingFrameRef.current = null;
        }
        flushAssistantUpdate();
        return;
      }

      if (streamingFrameRef.current !== null) return;
      streamingFrameRef.current = window.requestAnimationFrame(() => {
        streamingFrameRef.current = null;
        flushAssistantUpdate();
      });
    };

    const partToText = (part?: RunProgressPayload['part']): string => {
      const coerce = (value: unknown): string => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'string') return value;
        if (typeof value === 'number' || typeof value === 'boolean') return String(value);
        if (Array.isArray(value)) return value.map((item) => coerce(item)).join('');
        if (typeof value === 'object') {
          const obj = value as Record<string, unknown>;
          if (typeof obj.text === 'string') return obj.text;
          if (typeof (obj.text as any)?.value === 'string') return (obj.text as any).value as string;
          if (typeof obj.value === 'string') return obj.value as string;
          if (Array.isArray(obj.content)) return obj.content.map((item) => coerce(item)).join('');
        }
        try {
          return JSON.stringify(value);
        } catch {
          return String(value ?? '');
        }
      };

      if (!part) return '';
      const raw = part.content ?? (part as any).displayMessage ?? '';
      return coerce(raw);
    };

    const progressHandler = (progress: RunProgressPayload) => {
      if (progress.event === 'message.part' || progress.event === 'message.delta') {
        const chunk = partToText(progress.part);
        if (chunk) {
          streamingTextRef.current += chunk;
          scheduleAssistantUpdate({ text: streamingTextRef.current });
        }
      }

      if ((progress.event === 'run.completed' || progress.event === 'run.failed' || progress.event === 'run.cancelled') && progress.final?.output) {
        const last = progress.final.output[progress.final.output.length - 1];
        const parts = (last?.parts || []) as any[];
        const combined = parts.map((p) => partToText(p)).join('');
        if (combined) {
          streamingTextRef.current = combined;
          scheduleAssistantUpdate({ text: streamingTextRef.current }, true);
        }
      }
    };

    try {
      const resp: ChatResponse = await chat(payload, progressHandler, controller.signal);

      if (resp.userChatHistoryItemId) {
        setMessages((p) => p.map(m => m.id === tempId ? { ...m, id: resp.userChatHistoryItemId! } : m));
      }

      streamingTextRef.current = resp.response;
      scheduleAssistantUpdate({
        id: resp.chatHistoryItemId || assistantTempId,
        text: resp.response,
        sources: resp.contexts,
      }, true);

      if ((!resp.response || !resp.response.trim()) && (resp.chatSessionId || sessionId)) {
        const fallbackSessionId = resp.chatSessionId || sessionId;
        if (fallbackSessionId) {
          try {
            const history = await getSessionChatHistory(fallbackSessionId);
            const lastAssistant = [...history].reverse().find((item) => item.role !== 'user');
            if (lastAssistant) {
              streamingTextRef.current = lastAssistant.message;
              scheduleAssistantUpdate({ text: lastAssistant.message, sources: lastAssistant.contexts }, true);
            }
          } catch (historyErr) {
            console.warn('Kon fallback history niet laden:', historyErr);
          }
        }
      }

      // Titel en sessie bijwerken
      if (!messages.length && resp.chatSessionName && activeSessionId) {
        setSessions((p) => p.map((s) => (s.id === activeSessionId ? { ...s, name: resp.chatSessionName || s.name } : s)));
      }
      if (!activeSessionId && resp.chatSessionId) {
        setActiveSessionId(resp.chatSessionId);
        setSessions((p) => [{ id: resp.chatSessionId!, name: resp.chatSessionName || '', creationTime: new Date().toISOString() }, ...p]);
      }
    } catch (error) {
      const msg = (error as any)?.message || String(error);
      console.log('Error occurred while sending message: ', msg);
      notifyError(t('chat.errors.sendFailed'), msg);
      streamingTextRef.current = t('chat.messages.error');
      scheduleAssistantUpdate({ id: 'err-' + Date.now(), text: streamingTextRef.current, sources: [] }, true);
    } finally {
      setWaiting(false);
      if (streamingFrameRef.current !== null) {
        cancelAnimationFrame(streamingFrameRef.current);
        streamingFrameRef.current = null;
      }
      flushAssistantUpdate();
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
    }
  };

  // Feedback
  const askFeedback = (messageId: string, type: FeedbackType) => {
    setFeedbackGiven((prev) => ({ ...prev, [messageId]: prev[messageId] === type ? undefined : type }));
    setFeedbackModal({ messageId, type });
  };
  const sendFeedback = async () => {
    if (!feedbackModal) return;
    const req: ChatFeedbackRequest = { chatHistoryItemId: feedbackModal.messageId, isPositive: feedbackModal.type === 'up', comment: feedbackText.trim() || undefined };
    try { await submitChatFeedback(feedbackModal.messageId, feedbackModal.type, req); } catch {}
    setFeedbackText(''); setFeedbackModal(null);
  };

  const onComposerKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleSend(); }
  };

  // tekst -> HTML met paragrafen en <br/> (Outlook verwijdert inline CSS)
const textToHtml = (s: string) => {
  const esc = s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return esc.split(/\n{2,}/).map(p => `<p>${p.replace(/\n/g, '<br />')}</p>`).join('');
};

const replyWithAnswer = (text: string) => {
  const OfficeAny: any = (window as any).Office;
  const item: any = OfficeAny?.context?.mailbox?.item;
  if (!item) return;
  const html = textToHtml(text);

  if (typeof item.displayReplyForm === 'function') {
    item.displayReplyForm(html); // Read surface
    return;
  }
  if (item?.body?.prependAsync) {
    item.body.prependAsync(html, { coercionType: OfficeAny.CoercionType.Html }); // Compose
    return;
  }
  if (item?.setSelectedDataAsync) {
    item.setSelectedDataAsync(html, { coercionType: OfficeAny.CoercionType.Html });
    return;
  }
  if (item?.body?.setAsync) {
    item.body.setAsync(html, { coercionType: OfficeAny.CoercionType.Html });
  }
};

  const renderMessage = useCallback((text: string, role: string) => {
    return (
      <ReactMarkdown 
        remarkPlugins={[remarkGfm]}
        components={{
          table: ({ node, ...props }) => (
            <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 8, marginBottom: 8 }} {...props} />
          ),
          th: ({ node, ...props }) => (
            <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#f2f2f2', textAlign: 'left' }} {...props} />
          ),
          td: ({ node, ...props }) => (
            <td style={{ border: '1px solid #ddd', padding: '8px' }} {...props} />
          ),
          img: ({ node, ...props }) => {
            const { alt, ...rest } = props as React.ImgHTMLAttributes<HTMLImageElement>;
            return <img style={{ maxWidth: '50px', height: 'auto', verticalAlign: 'middle' }} alt={alt || ''} {...rest} />;
          },
          a: ({ node, ...props }) => {
            const { children, ...rest } = props as React.AnchorHTMLAttributes<HTMLAnchorElement>;
            return (
              <a
                style={{ color: role === 'user' ? '#fff' : '#1f74ff', textDecoration: 'underline' }}
                target="_blank"
                rel="noopener noreferrer"
                {...rest}
              >
                {children}
              </a>
            );
          }
        }}
      >
        {text}
      </ReactMarkdown>
    );
  }, []);

  // Nieuwe state voor tooltip
const [showMailTooltip, setShowMailTooltip] = useState(false);

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        background: '#ffffff',
        position: 'relative',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          backgroundImage: `url(${process.env.PUBLIC_URL}/assets/boma_mascot_Excited.png)`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'clamp(260px, 38vw, 520px)',
          backgroundPosition: 'center calc(100% - 160px)',
          opacity: 0.38,
          zIndex: 0,
        }}
      />
      {sidebarOpen && (
        <Sidebar
          sessions={sessions}
          open={sidebarOpen}
          onSelect={setActiveSessionId}
          onClose={() => setSidebarOpen(false)}
          onDelete={async (sessionId) => { await removeSession(sessionId); }}
        />
      )}

      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        zIndex: 1
      }}>
        <TopBar
          authed={authed}
          userId={userId}
          displayedTitle={displayedTitle}
          onOpenTitle={openTitle}
          onLogin={async () => { await authLogin().catch(() => {}); setAuthed(isAuthenticated()); setUserId(authGetUserId()); }}
          onLogout={() => { authLogout(); setAuthed(false); setUserId(undefined); }}
          onToggleSidebar={() => setSidebarOpen((s) => !s)}
          onNewChat={startNewChat}
        />
        
        <div style={{ 
          flex: 1, 
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <MessageList
            messages={messages}
            waiting={waiting}
            feedbackGiven={feedbackGiven}
            onAskFeedback={askFeedback}
            onShowSources={(s) => setSourcesModal(s)}
            onReplyWith={replyWithAnswer}
            renderMessage={renderMessage}
          />
          <div style={{ 
            position: 'sticky', 
            bottom: 0, 
            background: '#fff', 
            borderTop: '1px solid #eee', 
            padding: 8, 
            display: 'flex', 
            gap: 8, 
            alignItems: 'flex-end' 
          }}>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onComposerKey}
              placeholder={t('chat.composer.placeholder')}
              rows={3}
              style={{ 
                flex: 1, 
                minHeight: 44, 
                maxHeight: 160, 
                resize: 'vertical', 
                padding: '10px 12px', 
                border: '1px solid #d4d7dc', 
                borderRadius: 10, 
                fontSize: 14, 
                lineHeight: '20px' 
              }}
            />
            {/* Buttons verticaal gestapeld */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: 6,
              paddingBottom: 4
            }}>
              {/* Mail icoon toggle button */}
              <div style={{ position: 'relative', display: 'inline-flex' }}>
                <button
                  onClick={() => setIncludeMail(!includeMail)}
                  onMouseEnter={() => setShowMailTooltip(true)}
                  onMouseLeave={() => setShowMailTooltip(false)}
                  style={{
                    padding: 8,
                    border: 0,
                    background: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    color: includeMail ? '#1f74ff' : '#666',
                    transition: 'color 0.2s'
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </button>
                
                {showMailTooltip && (
                  <div style={{
                    position: 'absolute',
                    bottom: 'calc(100% + 8px)',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    padding: '8px 12px',
                    background: '#333',
                    color: '#fff',
                    fontSize: 13,
                    borderRadius: 6,
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                    zIndex: 1000,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  }}>
                    {includeMail ? t('chat.composer.emailToggle') : t('chat.composer.emailToggleOff')}
                    {/* Kleine pijl naar beneden */}
                    <div style={{
                      position: 'absolute',
                      bottom: -4,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 0,
                      height: 0,
                      borderLeft: '4px solid transparent',
                      borderRight: '4px solid transparent',
                      borderTop: '4px solid #333'
                    }} />
                  </div>
                )}
              </div>

              {/* Send button met icoon */}
              <button 
                onClick={() => void handleSend()} 
                disabled={!input.trim() || waiting}
                title={t('chat.composer.sendButton')}
                style={{ 
                  padding: '6px', 
                  borderRadius: 8, 
                  border: 0, 
                  background: '#1f74ff', 
                  color: '#fff', 
                  cursor: !input.trim() || waiting ? 'not-allowed' : 'pointer',
                  opacity: !input.trim() || waiting ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  transition: 'all 0.2s ease'
                }}
              >
                {/* Send/paper plane icon SVG */}
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M22 2L11 13"/>
                  <path d="M22 2L15 22L11 13L2 9L22 2z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <TitleModal open={titleModal} value={titleDraft} onChange={setTitleDraft} onClose={() => setTitleModal(false)} onSave={() => void saveTitle()} inputRef={titleRef} />
      <FeedbackModal open={!!feedbackModal} type={feedbackModal?.type || 'up'} value={feedbackText} onChange={setFeedbackText} onClose={() => setFeedbackModal(null)} onSend={() => void sendFeedback()} />
      <SourcesModal sources={sourcesModal} onClose={() => setSourcesModal(null)} />
    </div>
  );
}